import type { Promisable } from "@core/foundation/promise";
import type { ZodType, z } from "@core/zod";

import type {
  AnyApi,
  AnyApiEndpoint,
  ApiEndpoint,
  Api as ApiInterface,
} from "./api.ts";

export type GetToken<Api extends AnyApi> =
  Api extends ApiInterface<"public", infer _Domain, infer _Endpoints>
    ? () => null
    : () => Promisable<string>;

export function noToken(): null {
  return null;
}

// oxlint-disable-next-line custom/require-promisable
type ApiResponse<Return extends ZodType> = Promise<z.infer<Return>>;

type ApiCallerFn<
  Body extends ZodType | undefined,
  QueryParams extends ZodType | undefined,
  Response extends ZodType,
> = Body extends ZodType
  ? QueryParams extends ZodType
    ? (request: {
        readonly body: z.input<Body>;
        readonly queryParams: z.input<QueryParams>;
      }) => ApiResponse<Response>
    : (request: z.input<Body>) => ApiResponse<Response>
  : QueryParams extends ZodType
    ? (request: z.input<QueryParams>) => ApiResponse<Response>
    : () => ApiResponse<Response>;

export type AnyApiCallerFn = ApiCallerFn<
  ZodType | undefined,
  ZodType | undefined,
  ZodType
>;

export type ApiCaller<Api extends AnyApiEndpoint> =
  Api extends ApiEndpoint<
    infer Body,
    infer QueryParams,
    infer Response,
    infer _Streaming
  >
    ? ApiCallerFn<Body, QueryParams, Response>
    : never;

export type ApiCallers<Api extends AnyApi> = {
  [K in keyof Api["endpoints"]]: ApiCaller<Api["endpoints"][K]>;
};

export type AnyApiCallers = ApiCallers<AnyApi>;

export interface ApiClientContract<Api extends AnyApi> {
  readonly __typeTag: "ApiClientContract";
  readonly api: Api;
  readonly getApi: () => ApiCallers<Api>;
  readonly getCaller: <E extends keyof Api["endpoints"] & string>(
    endpointKey: E,
  ) => ApiCaller<Api["endpoints"][E]>;
  readonly getEndpointUrl: (
    endpointKey: keyof Api["endpoints"] & string,
  ) => string;
  readonly getToken: GetToken<Api>;
}
